/**
 * THE ONE TRANSPORT.
 *
 * Every response this app reads is parsed by its contract, and every refusal
 * is read out of the one error envelope. There is no second reader anywhere,
 * and no screen is allowed to touch `fetch` directly: a screen that read a
 * body its own way would be a second interpretation of what the server said.
 *
 * THREE SHAPES OF CALL, and no more, because the API has three.
 *   · `call`     — JSON in, contract out. GET and POST.
 *   · `enclose`  — a record: one file part and one declared role, multipart.
 *   · `save`     — the workpaper: a document a person keeps, not a payload.
 *
 * There is no PUT, PATCH or DELETE here because there is none in the product.
 * Nothing in this file interprets a body, decides a state, or formats a value.
 */
import type { z } from "zod";
import { EncloseRecordResponse, ErrorEnvelope, type FieldError } from "@leveri/contracts";

/** One reader for every response: parse the contract, or read the one error shape. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly requestId?: string,
    /**
     * WHICH FIELD WAS WRONG (R5.1). The server names it and gives the sentence
     * for it; the screen shows it against the input rather than making a
     * person guess which of four fields "The request is not valid" meant.
     */
    readonly fields: readonly FieldError[] = []
  ) { super(message); }
}

/** The server's sentence for one field, or nothing. Never composed here. */
export function fieldFailure(error: unknown, field: string): string | null {
  if (!(error instanceof ApiError)) return null;
  return error.fields.find((f) => f.field === field)?.message ?? null;
}

/**
 * THE SENTENCE A SCREEN SHOWS WHEN SOMETHING REFUSES.
 *
 * It is the server's own, always. The fallback exists for the one case the
 * envelope cannot cover — the request never reached a server that speaks it —
 * and says so without guessing at a cause.
 */
export function failureMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return "Leveri could not be reached. Nothing was recorded.";
}

/**
 * SIGNED OUT, ANYWHERE. A 401 from any call means this session is over, and
 * the whole app must say so once rather than each screen discovering it in
 * its own way. The shell registers a handler; the transport fires it.
 *
 * The screen never navigates on its own, so the address bar still holds where
 * the person was going — and after signing in they arrive there.
 */
let signedOutHandler: (() => void) | null = null;
export function whenSignedOut(handler: (() => void) | null): void {
  signedOutHandler = handler;
}

function csrfToken(): string | null {
  const m = document.cookie.match(/(?:^|;\s*)leveri_csrf=([^;]+)/);
  return m?.[1] ? decodeURIComponent(m[1]) : null;
}

/** The refusal, read from the one envelope and never from prose or a status alone. */
async function refuse(res: Response): Promise<never> {
  const json: unknown = await res.json().catch(() => null);
  if (res.status === 401 && signedOutHandler) signedOutHandler();
  const parsed = ErrorEnvelope.safeParse(json);
  if (parsed.success) throw new ApiError(res.status, parsed.data.error.code, parsed.data.error.message, parsed.data.error.requestId, parsed.data.error.fields ?? []);
  throw new ApiError(res.status, "UNKNOWN", `Request failed (${res.status})`);
}

export async function call<T extends z.ZodTypeAny>(method: "GET" | "POST", path: string, schema: T, body?: unknown): Promise<z.infer<T>> {
  const headers: Record<string, string> = { accept: "application/json" };
  if (body !== undefined) headers["content-type"] = "application/json";
  const token = csrfToken();
  if (method !== "GET" && token) headers["x-csrf-token"] = token;
  const init: RequestInit = { method, headers, credentials: "same-origin" };
  if (body !== undefined) init.body = JSON.stringify(body);
  const res = await fetch(path, init);
  if (!res.ok) return refuse(res);
  if (res.status === 204) return schema.parse(undefined);
  const json: unknown = await res.json().catch(() => null);
  return schema.parse(json);
}

/**
 * ENCLOSE A RECORD — the one multipart call in the product.
 *
 * One file part named `file`, one `role` field, exactly as the route reads
 * them. The role travels beside the bytes because the filename is never
 * trusted for it: what a record IS is declared by the person enclosing it,
 * and that declaration is the signal everything else is inferred from.
 *
 * `content-type` is deliberately NOT set. The browser writes it, with the
 * multipart boundary it chose; a hand-written header loses the boundary and
 * the server reads an empty body.
 */
export async function enclose<T extends z.ZodTypeAny>(
  path: string,
  schema: T,
  files: File | readonly File[],
  role: string,
  /** One role per file, in order, where they are not all the same kind. */
  roles?: readonly string[]
): Promise<z.infer<T>> {
  const chosen = Array.isArray(files) ? files : [files as File];
  const form = new FormData();
  form.append("role", role);
  if (roles && roles.length > 0) form.append("roles", JSON.stringify(roles));
  for (const file of chosen) form.append("file", file, file.name);
  const headers: Record<string, string> = { accept: "application/json" };
  const token = csrfToken();
  if (token) headers["x-csrf-token"] = token;
  const res = await fetch(path, { method: "POST", headers, credentials: "same-origin", body: form });
  if (!res.ok) return refuse(res);
  const json: unknown = await res.json().catch(() => null);
  return schema.parse(json);
}

/** How many files the server takes in one upload (its own limit); more are sent in turn. */
export const FILES_PER_UPLOAD = 10;

/**
 * ANY NUMBER OF FILES, in batches the server takes (found with the first tester, 2026-10-12: 34 statements meant
 * four manual rounds of ten). Each batch is its own act, as before; the answers are joined in the order sent, and
 * the work is the one after the last batch. A batch that fails stops the rest and says so; what was sent stays.
 */
export async function encloseAll(
  path: string,
  files: readonly File[],
  roles?: readonly string[],
  onBatch?: (done: number, total: number) => void,
): Promise<EncloseRecordResponse> {
  let merged: EncloseRecordResponse | null = null;
  for (let at = 0; at < files.length; at += FILES_PER_UPLOAD) {
    const batch = files.slice(at, at + FILES_PER_UPLOAD);
    const r = await enclose(path, EncloseRecordResponse, batch, roles ? roles[at] ?? "" : "", roles ? roles.slice(at, at + FILES_PER_UPLOAD) : undefined);
    merged = merged
      ? { ...r, record: merged.record ?? r.record, outcome: merged.outcome ?? r.outcome, records: [...merged.records, ...r.records], refused: [...merged.refused, ...r.refused], undetermined: [...merged.undetermined, ...r.undetermined] }
      : r;
    onBatch?.(Math.min(at + FILES_PER_UPLOAD, files.length), files.length);
  }
  if (!merged) throw new Error("No files to send.");
  return merged;
}

/**
 * SAVE THE WORKPAPER.
 *
 * The response is a document, not a payload: the server names it in
 * `content-disposition`, and that name is what lands on the desktop. A name
 * invented here would be a second opinion about what the file is called.
 *
 * A refusal still arrives as the one envelope, so the failing path is read by
 * the same reader as everything else.
 */
export async function save(path: string): Promise<void> {
  const res = await fetch(path, { headers: { accept: "*/*" }, credentials: "same-origin" });
  if (!res.ok) await refuse(res);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filenameFrom(res.headers.get("content-disposition"));
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoked on the next turn of the loop, not in this one: a URL torn down in
  // the same tick as the click is a download that silently never starts.
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

/**
 * The filename the server sent. `filename*` (RFC 5987) is read first because
 * it is the form that survives a non-ASCII name; the plain form is the
 * fallback, and an absent header leaves the browser to name the file.
 */
function filenameFrom(disposition: string | null): string {
  if (!disposition) return "";
  const extended = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(disposition);
  if (extended?.[1]) {
    try { return decodeURIComponent(extended[1].trim()); } catch { /* fall through to the plain form */ }
  }
  const plain = /filename\s*=\s*"([^"]*)"|filename\s*=\s*([^;]+)/i.exec(disposition);
  return (plain?.[1] ?? plain?.[2] ?? "").trim();
}
